import { SiteFooter, SiteHeader, SponsorSlot } from "./_components/SiteChrome";

const freeFeatures = [
  ["Multi-camera by design", "Choose, preview, add, and change every camera input without rebuilding a scene."],
  ["Audience Editions", "Produce landscape and portrait outputs from one show, with independent layouts and delivery profiles."],
  ["Broadcast Guardian", "Preflight checks, frozen-picture detection, audio monitoring, and actionable readiness reports."],
  ["Creator-owned delivery", "Stream through your own destination credentials with local production and recording at the center."],
];

const plannedFeatures = [
  ["Sponsor Proof", "Track campaign requirements live and generate a professional proof pack with timestamps, airtime, clips, and receipts.", "Revenue"],
  ["Stream Insurance Pro", "Automatic fallback scenes, recovery snapshots, destination reconnects, and a post-show incident report.", "Reliability"],
  ["One Stream → Content Week", "Turn marked moments into landscape and vertical publishing packs without uploading an entire show to a cloud editor.", "Growth"],
  ["Remote Producer Link", "Invite a trusted producer to control scenes, cues, and clip markers from a secure browser or phone session.", "Team"],
];

const faqs = [
  ["Is GeoStream going to remain free?", "The core desktop broadcasting workflow is planned to remain free: scenes, cameras, essential recording, and the tools needed to run a real show. Paid memberships will focus on business outcomes, advanced automation, and collaboration."],
  ["Can I buy a membership today?", "Not yet. GeoStream is in beta and there are no membership charges. The prices shown below are targets for feedback, not active offers."],
  ["Will advertisements appear inside my stream?", "Never automatically. Website partner placements are clearly labeled. Nothing is inserted into a creator’s broadcast unless that creator deliberately adds or enables it."],
  ["Where are the public downloads?", "The downloads page automatically shows the verified package for your computer. The signed and notarized macOS beta is available now; Windows will activate after its separate signing and clean-install verification are complete."],
];

export default function Home() {
  return (
    <>
      <SiteHeader />
      <main>
        <section className="hero">
          <div className="hero-grid" aria-hidden="true" />
          <div className="shell hero-layout">
            <div className="hero-copy">
              <div className="status-pill"><span /> Private beta · Core broadcasting stays free</div>
              <h1>Broadcast everywhere.<br /><em>Prove what it earned.</em></h1>
              <p className="hero-lead">GeoStream is a modern desktop studio for creators who need more than a “Go Live” button—reliable production, audience-specific outputs, and tools designed to turn a broadcast into a business.</p>
              <div className="hero-actions">
                <a className="button button-primary" href="/downloads">Explore the beta <span aria-hidden="true">→</span></a>
                <a className="button button-secondary" href="#memberships">See what is coming</a>
              </div>
              <div className="trust-row" aria-label="GeoStream product principles">
                <span>Local-first production</span>
                <span>No forced watermark</span>
                <span>Creator-controlled credentials</span>
              </div>
            </div>

            <div className="studio-card" aria-label="GeoStream studio status preview">
              <div className="studio-topbar">
                <span className="studio-brand">GEOSTREAM / LIVE CONTROL</span>
                <span className="live-chip"><i /> LIVE 01:42:18</span>
              </div>
              <div className="studio-program">
                <div className="program-art">
                  <span className="program-label">PROGRAM</span>
                  <div className="program-rings"><i /><i /></div>
                  <strong>One show.<br />Every audience.</strong>
                </div>
                <div className="edition-stack">
                  <div><span>LANDSCAPE</span><b>16:9</b></div>
                  <div><span>PORTRAIT</span><b>9:16</b></div>
                </div>
              </div>
              <div className="studio-metrics">
                <div><span>DESTINATIONS</span><strong>4 / 4</strong><small className="healthy">● Healthy</small></div>
                <div><span>GUARDIAN</span><strong>Ready</strong><small>Picture · audio · cadence</small></div>
                <div><span>SPONSOR PROOF</span><strong>18:42</strong><small>30:00 target · planned</small></div>
              </div>
              <div className="studio-wave" aria-hidden="true">
                {Array.from({ length: 28 }).map((_, index) => <i key={index} style={{ height: `${18 + ((index * 13) % 44)}%` }} />)}
              </div>
            </div>
          </div>
        </section>

        <section className="partner-strip-section">
          <div className="shell">
            <SponsorSlot
              compact
              eyebrow="Clearly labeled commercial placement"
              title="Featured technology sponsor"
              description="A future sponsored placement for capture hardware, connectivity, or creator tools that genuinely improve live production. Sponsorship never implies ownership of GeoStream."
            />
          </div>
        </section>

        <section className="section" id="platform">
          <div className="shell">
            <div className="section-heading split-heading">
              <div>
                <p className="eyebrow">The free foundation</p>
                <h2>A serious studio before a subscription.</h2>
              </div>
              <p>Free competitors set the right expectation: creators should not have to rent basic camera switching and scene composition. GeoStream earns a membership by doing higher-value work.</p>
            </div>
            <div className="feature-grid">
              {freeFeatures.map(([title, description], index) => (
                <article className="feature-card" key={title}>
                  <span className="feature-index">0{index + 1}</span>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section editions-section">
          <div className="shell editions-layout">
            <div className="edition-visual" aria-label="Landscape and portrait broadcast outputs">
              <div className="edition-frame edition-landscape"><span>LANDSCAPE / PROGRAM</span><b>1280 × 720</b><i /></div>
              <div className="edition-frame edition-portrait"><span>PORTRAIT / PROGRAM</span><b>720 × 1280</b><i /></div>
              <div className="edition-link">ONE CONTROL ROOM</div>
            </div>
            <div>
              <p className="eyebrow">Audience Editions</p>
              <h2>Stop making one canvas pretend it fits everywhere.</h2>
              <p className="section-lead">Build simultaneous landscape and vertical experiences from the same live production. Choose which sources appear, how they fit, and where each edition goes.</p>
              <ul className="check-list">
                <li>Independent scene overrides per edition</li>
                <li>Destination-specific output profiles</li>
                <li>Local capacity checks before going live</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="section roadmap-section" id="memberships">
          <div className="shell">
            <div className="section-heading centered-heading">
              <p className="eyebrow">Paid memberships · planned</p>
              <h2>Pay for outcomes, not permission to stream.</h2>
              <p>These are the premium systems we are designing with beta users. They are not active charges or final promises yet.</p>
            </div>
            <div className="planned-grid">
              {plannedFeatures.map(([title, description, tag]) => (
                <article className="planned-card" key={title}>
                  <span>{tag}</span>
                  <h3>{title}</h3>
                  <p>{description}</p>
                  <small>IN DEVELOPMENT</small>
                </article>
              ))}
            </div>

            <div className="pricing-grid" aria-label="Planned GeoStream membership pricing">
              <article className="price-card">
                <span className="price-label">AVAILABLE DURING BETA</span>
                <h3>Core</h3>
                <div className="price"><strong>$0</strong><span>forever planned</span></div>
                <ul><li>Scenes and multi-camera production</li><li>Local recording and essential streaming</li><li>Audience Edition foundations</li><li>Basic Broadcast Guardian</li></ul>
                <a className="button button-secondary" href="/downloads">Get the beta</a>
              </article>
              <article className="price-card price-featured">
                <span className="price-label">TARGET PRICE · NOT FOR SALE</span>
                <h3>Creator Pro</h3>
                <div className="price"><strong>$7.99</strong><span>/ month target</span></div>
                <ul><li>Stream Insurance Pro</li><li>Local publishing packs</li><li>Advanced Audience Editions</li><li>Remote Producer Link</li></ul>
                <span className="button button-disabled">Coming after beta</span>
              </article>
              <article className="price-card">
                <span className="price-label">TARGET PRICE · NOT FOR SALE</span>
                <h3>Revenue Pro</h3>
                <div className="price"><strong>$14.99</strong><span>/ month target</span></div>
                <ul><li>Everything in Creator Pro</li><li>Sponsor campaign assurance</li><li>Proof clips and branded reports</li><li>Campaign and partner workspaces</li></ul>
                <span className="button button-disabled">Planned membership</span>
              </article>
            </div>
            <p className="pricing-note">Target pricing is subject to beta feedback. GeoStream is not accepting membership payments today.</p>
          </div>
        </section>

        <section className="section company-section" id="company">
          <div className="shell">
            <div className="section-heading split-heading">
              <div>
                <p className="eyebrow">The company</p>
                <h2>Built carefully by an independent founder.</h2>
              </div>
              <p>GeoStream is an independent broadcasting company shaped by three priorities: dependable engineering, respect for creators, and practical tools people can afford.</p>
            </div>
            <div className="leadership-grid">
              <article className="leadership-card">
                <span className="leadership-initials" aria-hidden="true">JN</span>
                <div><p className="eyebrow">Founder &amp; Software Engineer</p><h3>James New</h3><p>James earned a degree in Computer Science from the University of Colorado and has built software in Fortune 100 and Fortune 500 environments. At GeoStream, he applies that experience to reliability, security, and a simpler experience for streamers.</p></div>
              </article>
              <aside className="leadership-note">
                <p className="eyebrow">Founder-led, user-informed</p>
                <h3>One founder, shaped by working streamers.</h3>
                <p>GeoStream is independently founded and currently built by James. Beta feedback helps guide the product without implying company ownership or titles that do not exist.</p>
              </aside>
            </div>
            <div className="company-principles"><span>Creator trust comes first</span><span>Practical engineering</span><span>Security by default</span><span>Affordable by design</span></div>
          </div>
        </section>

        <section className="section faq-section" id="faq">
          <div className="shell faq-layout">
            <div><p className="eyebrow">Good questions</p><h2>Clear now, before checkout exists.</h2></div>
            <div className="faq-list">
              {faqs.map(([question, answer]) => <details key={question}><summary>{question}<span>+</span></summary><p>{answer}</p></details>)}
            </div>
          </div>
        </section>

        <section className="final-cta">
          <div className="shell final-cta-inner">
            <div><p className="eyebrow">Build with us</p><h2>The beta is where honest pricing gets decided.</h2><p>Test the studio, tell us what saves real time or protects real revenue, and help shape the memberships before they exist.</p></div>
            <a className="button button-primary" href="/downloads">Visit downloads <span aria-hidden="true">→</span></a>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
